
import { BirdieGameGroup } from "@/types";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronRight } from "lucide-react";
import { GameStatus } from "./GameStatusCalculator";
import AvatarPlaceholder from "@/components/ui/avatar-placeholder";

interface GameCardProps {
  game: BirdieGameGroup;
  status: GameStatus;
  onViewGame: (gameId: string) => void;
  currentUserId?: string;
}

const GameCard = ({ game, status, onViewGame, currentUserId }: GameCardProps) => {
  const { statusMessage, statusClass, currentUserHoles } = status;

  const progressPercentage = Math.min(100, (currentUserHoles / 18) * 100);

  return (
    <Card
      key={game.id}
      className="cursor-pointer border border-border/50 shadow-sm hover:shadow-md transition-shadow rounded-xl overflow-hidden"
      onClick={() => onViewGame(game.id)}
    >
      <div className="flex">
        {/* Left accent bar */}
        <div className="w-1 bg-forest shrink-0" />

        <CardContent className="p-4 flex-1 min-w-0">
          <div className="flex justify-between items-start">
            <div className="min-w-0 flex-1 pr-2">
              <div className="flex items-center gap-1.5">
                <h3 className="font-semibold text-forest truncate">{game.name}</h3>
                {game.isSample && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 shrink-0 text-muted-foreground border-muted-foreground/30">
                    Sample
                  </Badge>
                )}
              </div>
              {game.members.length > 1 && (
                <p className={`text-sm ${statusClass}`} data-testid="game-status-message">
                  {statusMessage}
                </p>
              )}
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground/50 shrink-0 mt-0.5" />
          </div>

          {/* Progress */}
          <div className="mt-2.5 flex items-center gap-2">
            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-forest rounded-full transition-all duration-700"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {currentUserHoles}/18
            </span>
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center mt-2.5">
            <p className="text-xs text-muted-foreground truncate">{game.courseName || ""}</p>
            <div className="flex -space-x-1">
              {game.members.map((member) => (
                <div
                  key={member.id}
                  className={`rounded-full ${
                    member.id === currentUserId
                      ? 'ring-1 ring-gold'
                      : 'ring-1 ring-white'
                  }`}
                  title={member.name}
                >
                  <AvatarPlaceholder
                    user={member}
                    size="sm"
                    className="h-5 w-5"
                  />
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </div>
    </Card>
  );
};

export default GameCard;
